import { Component } from '@theme/component';
import { fieldError } from '@theme/eme-form-validation-core';

class EmeFormValidation extends Component {
  #form = null;
  #submitLabel = '';

  connectedCallback() {
    super.connectedCallback();
    this.#form = this.querySelector('form');
    if (!this.#form) return;
    this.#form.noValidate = true;
    // Must run before Shopify's hCaptcha binds the form: it keeps this function as the final submit after the token.
    this.#form.submit = this.#submit;
    this.addEventListener('submit', this.#handleSubmit, { capture: true });
    this.#form.addEventListener('input', this.#handleInput);
    window.addEventListener('pageshow', this.#handlePageShow);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('submit', this.#handleSubmit, { capture: true });
    this.#form?.removeEventListener('input', this.#handleInput);
    if (this.#form?.submit === this.#submit) delete this.#form.submit;
    window.removeEventListener('pageshow', this.#handlePageShow);
  }

  #submit = () => {
    if (this.#form.dataset.sending === 'true') return;
    const firstInvalid = this.#validate();
    if (firstInvalid) {
      firstInvalid.focus();
      return;
    }
    this.#setSending(true);
    HTMLFormElement.prototype.submit.call(this.#form);
  };

  #handleSubmit = (event) => {
    if (event.target !== this.#form) return;
    if (this.#form.dataset.sending === 'true') {
      event.preventDefault();
      event.stopPropagation();
      return;
    }
    const firstInvalid = this.#validate();
    if (firstInvalid) {
      event.preventDefault();
      event.stopPropagation();
      firstInvalid.focus();
      return;
    }
    setTimeout(() => {
      if (!event.defaultPrevented) this.#setSending(true);
    });
  };

  #validate() {
    let firstInvalid = null;
    for (const field of this.#form.querySelectorAll('[data-eme-validate]')) {
      if (!(field instanceof HTMLInputElement || field instanceof HTMLTextAreaElement)) continue;
      const error = fieldError({ value: field.value, required: field.required, type: field.type });
      this.#show(field, error);
      if (error && !firstInvalid) firstInvalid = field;
    }
    return firstInvalid;
  }

  #handleInput = (event) => {
    const field = event.target;
    if (field instanceof HTMLElement && field.getAttribute('aria-invalid') === 'true') this.#show(field, null);
  };

  #handlePageShow = (event) => {
    if (event.persisted) this.#setSending(false);
  };

  #show(field, error) {
    const message = this.querySelector(`#${CSS.escape(field.id)}-error`);
    if (!(message instanceof HTMLElement)) return;
    const base = field.dataset.describedbyBase ?? '';
    if (!error) {
      message.hidden = true;
      field.removeAttribute('aria-invalid');
      if (base) field.setAttribute('aria-describedby', base);
      else field.removeAttribute('aria-describedby');
      return;
    }
    const text = message.querySelector('[data-error-text]');
    if (text) text.textContent = (error === 'email' ? field.dataset.errorEmail : field.dataset.errorRequired) ?? '';
    message.hidden = false;
    field.setAttribute('aria-invalid', 'true');
    field.setAttribute('aria-describedby', [base, message.id].filter(Boolean).join(' '));
  }

  #setSending(sending) {
    this.#form.dataset.sending = String(sending);
    this.#form.setAttribute('aria-busy', String(sending));
    const submit = this.#form.querySelector('[type="submit"]');
    if (!(submit instanceof HTMLButtonElement)) return;
    if (sending) {
      this.#submitLabel = submit.textContent ?? '';
      submit.textContent = this.dataset.sendingLabel ?? this.#submitLabel;
    } else if (this.#submitLabel) {
      submit.textContent = this.#submitLabel;
    }
    submit.disabled = sending;
  }
}

if (!customElements.get('eme-form-validation')) customElements.define('eme-form-validation', EmeFormValidation);
